import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Truck, Store, ShoppingCart, Coffee, Fuel, Users } from 'lucide-react';
// Zone colors by category
const ZONE_COLORS = {
  warehouse: { fill: '#22c55e40', stroke: '#16a34a' },
  restricted: { fill: '#ef444440', stroke: '#dc2626' },
  delivery: { fill: '#f59e0b40', stroke: '#d97706' },
  service: { fill: '#3b82f640', stroke: '#2563eb' }
};

const PlacesLayer = ({ placesTypes = [], onMarkerSelect }) => {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const [placesService, setPlacesService] = useState(null);
  const [placesMarkers, setPlacesMarkers] = useState([]);

  useEffect(() => {
    if (!placesLib || !map) return;
    setPlacesService(new placesLib.PlacesService(map));
  }, [placesLib, map]);

  useEffect(() => {
    if (!placesService || !placesTypes || placesTypes.length === 0) {
      setPlacesMarkers([]);
      return;
    }

    const fetchAll = async () => {
      let results = [];
      const center = map.getCenter();
      if (!center) return;
      
      for (const type of placesTypes) {
        if (type === 'clients') continue;
        
        const request = {
          location: center,
          radius: 5000,
          type: type
        };

        try {
          const res = await new Promise((resolve) => {
            placesService.nearbySearch(request, (data, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(data);
              } else {
                resolve([]);
              }
            });
          });
          
          results = [...results, ...res.map(r => ({ ...r, searchType: type }))];
        } catch (e) {
          console.error(e);
        }
      }
      setPlacesMarkers(results);
    };

    const listener = map.addListener('idle', fetchAll);
    fetchAll();

    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, [placesService, placesTypes, map]);

  const typeIcons = {
    bakery: { Icon: Coffee, color: '#f59e0b' },
    grocery_or_supermarket: { Icon: ShoppingCart, color: '#0ea5e9' },
    convenience_store: { Icon: Store, color: '#8b5cf6' },
    gas_station: { Icon: Fuel, color: '#ef4444' }
  };

  return (
    <>
      {placesMarkers.map((place, i) => {
        const typeInfo = typeIcons[place.searchType] || { Icon: Store, color: '#3b82f6' };
        const IconComponent = typeInfo.Icon;
        return (
          <AdvancedMarker
            key={`${place.place_id}-${i}`}
            position={place.geometry.location}
            onClick={() => onMarkerSelect?.({
              name: place.name,
              location: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() },
              customInfo: place.vicinity,
              photoUrl: place.photos && place.photos.length > 0 ? place.photos[0].getUrl({ maxWidth: 300, maxHeight: 200 }) : null
            })}
          >
            <div
              className="cursor-pointer transform transition-all hover:scale-110"
              style={{
                width: '30px',
                height: '30px',
                background: typeInfo.color,
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IconComponent className="h-4 w-4 text-white" />
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
};

const PlaceInfoWindow = ({ marker, allMarkers = [], clientsData = [], onCloseClick, t }) => {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const [photoUrl, setPhotoUrl] = useState(marker.photoUrl || null);
  const [radiusKm, setRadiusKm] = useState('');
  const [radiusUsers, setRadiusUsers] = useState(null);
  const [radiusCircle, setRadiusCircle] = useState(null);
  const [isRadiusOpen, setIsRadiusOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (radiusCircle) radiusCircle.setMap(null);
    };
  }, [radiusCircle]);

  useEffect(() => {
    setRadiusKm('');
    setRadiusUsers(null);
    if (radiusCircle) {
      radiusCircle.setMap(null);
      setRadiusCircle(null);
    }
  }, [marker.name, marker.location]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleAnalyze = () => {
    const r = parseFloat(radiusKm);
    if (isNaN(r) || r <= 0) return;
    
    if (radiusCircle) radiusCircle.setMap(null);
    
    const circle = new window.google.maps.Circle({
      map,
      center: { lat: marker.location.lat, lng: marker.location.lng },
      radius: r * 1000,
      fillColor: '#ef4444',
      fillOpacity: 0.15,
      strokeColor: '#ef4444',
      strokeWeight: 2,
    });
    setRadiusCircle(circle);
    
    // Combine all potential markers to check
    const combinedMarkers = [...allMarkers, ...clientsData];
    
    // Calculate users inside radius
    let sum = 0;
    let count = 0;
    combinedMarkers.forEach(m => {
      // Handle both location objects {lat, lng} and {lat(), lng()}
      let mLat, mLng;
      if (!m.location && m.lat && m.lng) {
        mLat = m.lat;
        mLng = m.lng;
      } else if (m.location && typeof m.location.lat === 'function') {
        mLat = m.location.lat();
        mLng = m.location.lng();
      } else if (m.location && m.location.lat) {
        mLat = m.location.lat;
        mLng = m.location.lng;
      } else {
        return;
      }
      
      const distance = calculateDistance(marker.location.lat, marker.location.lng, mLat, mLng);
      if (distance <= r) {
        let val = 1; // Default to 1 if it's just a regular marker without a value
        if (m.value !== undefined) {
          val = m.value;
        } else if (m.customInfo) {
          const match = m.customInfo.match(/(\d+)\s*(Clientes|Users|Consumers|Members)/i);
          if (match) {
             val = parseInt(match[1], 10);
          }
        }
        sum += val;
        count++;
        console.log(`Included marker: ${m.name}, distance: ${distance.toFixed(2)}km, val: ${val}`);
      }
    });
    console.log(`Radius analysis: radius=${r}km, found ${count} markers, total sum=${sum}`);
    setRadiusUsers(sum);
  };

  useEffect(() => {
    setPhotoUrl(marker.photoUrl || null);
    if (marker.photoUrl) return;
    if (!placesLib || !map || !marker.name) return;

    const placesService = new placesLib.PlacesService(map);
    const request = {
      query: marker.name,
      fields: ['photos'],
      locationBias: {
        radius: 1000,
        center: { lat: marker.location.lat, lng: marker.location.lng }
      }
    };

    placesService.findPlaceFromQuery(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        if (results[0].photos && results[0].photos.length > 0) {
          setPhotoUrl(results[0].photos[0].getUrl({ maxWidth: 300, maxHeight: 200 }));
        }
      }
    });
  }, [marker.name, marker.location, marker.photoUrl, placesLib, map]);

  return (
    <InfoWindow
      position={{ lat: marker.location.lat, lng: marker.location.lng }}
      onCloseClick={onCloseClick}>
      <div className="text-sm p-2 min-w-[200px] max-w-[280px]">
        {photoUrl && (
          <div className="mb-3 rounded overflow-hidden shadow-sm">
            <img src={photoUrl} alt={marker.name} className="w-full h-[140px] object-cover" />
          </div>
        )}
        <strong className="block text-base">{marker.name}</strong>
        {marker.status && <div className="mt-1">{t("status", "Status:")} {marker.status}</div>}
        {marker.speed !== undefined && <div className="mt-1">{t("speed", "Speed:")} {marker.speed} {t("kmh", "km/h")}</div>}
        {marker.driver && <div className="mt-1">{t("driver", "Driver:")} {marker.driver}</div>}
        {marker.customInfo && <div className="mt-2 text-gray-600 whitespace-pre-wrap">{marker.customInfo}</div>}
        
        {/* Radius Analysis UI (Discrete) */}
        <div className="mt-3 pt-2 border-t border-gray-100 flex flex-col items-end">
          <button 
            onClick={() => setIsRadiusOpen(!isRadiusOpen)}
            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full"
            title="Radius Analysis (Members)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
          </button>
          
          {isRadiusOpen && (
            <div className="flex gap-2 mt-2 w-full justify-end items-center">
              <div className="relative">
                <input 
                  type="number" 
                  placeholder="Radius" 
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(e.target.value)}
                  className="w-24 border border-gray-300 rounded pl-2 pr-7 py-1 text-xs"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                  km
                </span>
              </div>
              <button 
                onClick={handleAnalyze}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs transition-colors font-medium"
              >
                Analyze
              </button>
            </div>
          )}
          {radiusUsers !== null && (
            <div className="bg-red-50 text-red-700 p-1 px-2 rounded text-center font-bold text-xs mt-2 w-full">
              Radius Members = {radiusUsers.toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </InfoWindow>
  );
};

// Google Maps wrapper with zones support
const GoogleMap = ({
  apiKey,
  markers = [],
  zones = [],
  selectedMarker,
  onMarkerSelect,
  selectedZone,
  onZoneSelect,
  isDrawing = false,
  drawingType = 'circle',
  drawingPoints = [],
  onPointAdd,
  center = { lat: -23.5505, lng: -46.6333 },
  zoom = 12,
  className = '',
  placesTypes = [],
  clientsData = []
}) => {const { t } = useTranslation("wms");
  const [map, setMap] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);

  // Draw zones on map
  useEffect(() => {
    if (!map || !window.google) return;

    // Clear existing overlays
    // In a real implementation, we'd track these and clear them

    zones.forEach((zone) => {
      const colors = ZONE_COLORS[zone.category] || ZONE_COLORS.service;

      if (zone.type === 'circle' && zone.center) {
        new window.google.maps.Circle({
          map,
          center: { lat: zone.center.lat, lng: zone.center.lng },
          radius: zone.radius || 500,
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          clickable: true
        });
      } else if (zone.type === 'polygon' && zone.points?.length > 2) {
        new window.google.maps.Polygon({
          map,
          paths: zone.points.map((p) => ({ lat: p.lat, lng: p.lng })),
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          clickable: true
        });
      }
    });
  }, [map, zones]);

  // Handle map click for drawing
  useEffect(() => {
    if (!map || !isDrawing) return;

    const listener = map.addListener('click', (e) => {
      onPointAdd?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });

    return () => {
      window.google?.maps.event.removeListener(listener);
    };
  }, [map, isDrawing, onPointAdd]);

  if (!apiKey) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} style={{ minHeight: '400px' }}>
        <p className="text-gray-500">{t("googleMapsApi", "Google Maps API key required")}</p>
      </div>);

  }

  const statusColors = {
    online: '#22c55e',
    offline: '#9ca3af',
    moving: '#3b82f6',
    idle: '#f59e0b',
    alert: '#ef4444'
  };

  return (
    <APIProvider apiKey={apiKey}>
      <Map
        defaultCenter={center}
        defaultZoom={zoom}
        mapId="geofencing-map"
        className={className}
        style={{ minHeight: '400px' }}
        onCameraChanged={(ev) => {

          // Can track camera changes if needed
        }} gestureHandling="greedy">
        
        {/* Markers */}
        {markers.map((marker) => {
          if (!marker.location?.lat || !marker.location?.lng) return null;

          const IconComponent = marker.icon || Truck;

          return (
            <AdvancedMarker
              key={marker.id}
              position={{ lat: marker.location.lat, lng: marker.location.lng }}
              onClick={() => onMarkerSelect?.(marker)}>
              
              <div
                className="cursor-pointer transform transition-all hover:scale-110"
                style={{
                  width: '36px',
                  height: '36px',
                  background: marker.color || statusColors[marker.status] || statusColors.online,
                  borderRadius: '50%',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                
                <IconComponent className="h-5 w-5 text-white" />
              </div>
            </AdvancedMarker>);

        })}

        {/* Places API Layer */}
        <PlacesLayer placesTypes={placesTypes} onMarkerSelect={onMarkerSelect} />
        
        {/* Clients Layer */}
        {placesTypes.includes('clients') && clientsData.map((client, i) => (
          <AdvancedMarker
            key={`client-${i}`}
            position={{ lat: client.lat, lng: client.lng }}
            onClick={() => onMarkerSelect?.({
              name: client.name,
              location: { lat: client.lat, lng: client.lng },
              customInfo: `${client.category} Partner\n${client.address}`
            })}
          >
            <div
              className="cursor-pointer transform transition-all hover:scale-125"
              style={{
                width: '32px',
                height: '32px',
                background: '#16a34a',
                borderRadius: '50%',
                border: '3px solid white',
                boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <Store className="h-4 w-4 text-white" />
            </div>
          </AdvancedMarker>
        ))}

        {/* Info Window for selected marker */}
        {selectedMarker && selectedMarker.location &&
          <PlaceInfoWindow
            marker={selectedMarker}
            allMarkers={markers}
            clientsData={clientsData}
            onCloseClick={() => onMarkerSelect?.(null)}
            t={t}
          />
        }
      </Map>
    </APIProvider>);

};

export default GoogleMap;